import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type FinancialResponse } from '../api.ts';
import { Notice, ErrorNotice, Loading, Field, Select } from '../components/Bits.tsx';

export function Financial() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data, isPending, error } = useQuery({
    queryKey: ['financial'],
    queryFn: () => api.get<FinancialResponse>('/api/application/financial'),
  });

  const [income, setIncome] = useState<string>('');
  const [siblings, setSiblings] = useState<string>('');

  useEffect(() => {
    if (!data?.current) return;
    setIncome(String(data.current.income_option_id ?? ''));
    setSiblings(String(data.current.sibling_option_id ?? ''));
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put('/api/application/financial', {
      incomeOptionId: Number(income),
      siblingOptionId: Number(siblings),
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['application'] });
      await queryClient.invalidateQueries({ queryKey: ['financial'] });
      navigate('/apply');
    },
  });

  if (isPending) return <main className="narrow" id="main"><Loading /></main>;
  if (error) return <main className="narrow" id="main"><ErrorNotice error={error} /></main>;

  return (
    <main className="narrow" id="main">
      <p><Link to="/apply">← Back to your application</Link></p>
      <h2>Financial information</h2>
      <p className="lede">
        Financial need is one of the things the board weighs. Choose the range that fits your
        household best.
      </p>

      <Notice kind="info">
        This is not verified against any document and is not shared outside the review board.
      </Notice>

      <ErrorNotice error={save.error} />

      <form className="panel" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <Field label="Total household income *" hint="Before tax, for the most recent year.">
          <Select value={income} required onChange={(e) => setIncome(e.target.value)}>
            <option value="">Choose a range…</option>
            {data?.options.income.map((o) => (
              <option key={o.id} value={o.id}>{o.income}</option>
            ))}
          </Select>
        </Field>

        <Field label="Siblings in college *" hint="Not counting you.">
          <Select value={siblings} required onChange={(e) => setSiblings(e.target.value)}>
            <option value="">Choose…</option>
            {data?.options.siblings.map((o) => (
              <option key={o.id} value={o.id}>{o.siblings}</option>
            ))}
          </Select>
        </Field>

        <div className="actions">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="secondary" onClick={() => navigate('/apply')}>Cancel</button>
        </div>
      </form>
    </main>
  );
}
